/* Which of a visit's booked services auto-start puts up, and what the desk is
 * offered when the technician one of them is booked with is still busy.
 *
 * Kevin, 2026-10-20: auto-start only starts a service booked AT THE SAME TIME
 * as the one the client was seated for. Kennedy White was booked for a Pamper
 * Me Pedicure with Helen and an Acrylic Full Set with Rosy, both at 10:00, and
 * checked in for both at once: seating Helen's square seats Rosy's too. A
 * service booked for later in the visit waits for its own seating, and so does
 * one booked earlier that nobody started — that is the desk's to deal with, not
 * something to start behind their back.
 *
 * The anchor is the booked start of each square on the visit whose clock has
 * started — running or since stopped; either way the client was seated for it.
 * With nothing on the board for the visit at all, Mangomint's own check-in is
 * the seating (Erika Heaton, checked in for her 10:00 with Jocy and nothing put
 * up) — but only for the services whose booked time has already come. Kevin,
 * 2026-10-23: Mangomint checks the whole appointment in, so James Leshikar's
 * check-in for his 3:30 Hard Gel Fill with Faith also read as a check-in for
 * his 5:00 pedicure with Ann, and Ann's square went up an hour and a half
 * early. A check-in is the client in the building, not in every chair on the
 * booking; anything booked later waits for its own seating.
 *
 * Never by matching appointments to each other, either: a group booking shows
 * under one client more than once and is several people. A check-in older than
 * `CHECKIN_FRESH_MS` is history rather than a client sitting down — a console
 * opened, or this rule shipped, mid-morning must not put up squares for visits
 * the desk has been leaving alone for an hour.
 *
 * The booked-clients setting (`bookedClientsGo`) changes one thing here. Under
 * `book` a check-in starts the technician the book names, as above. Under `turn`
 * the book's name is not who takes the client, so a check-in on a booking nobody
 * asked for by name starts nothing: the booking stays on Due now and the desk
 * assigns it, to next up. A request still starts as it always did, because that
 * is a promise to a technician by name. See `waitsForDesk`.
 *
 * Pure. `lines` is the visit as the store sees it:
 *   { id, startsAt,
 *     onBoard  — a square stands for it, running or held,
 *     started  — that square's clock has started (not held),
 *     closed   — checked out, or a square taken off it on purpose }
 */

export const CHECKIN_FRESH_MS = 10 * 60000;

/* Kevin, 2026-11-02: a check-in shortly before the booked time starts the
 * service when that time comes. Jimena De La Lama was checked in at 5:05 for a
 * 5:10 pedicure with Helen; nothing went up at 5:05 because 5:10 had not come,
 * and nothing went up at 5:10 because the check-in was already old news. Up to
 * this far ahead is the client sitting down a few minutes early. Further ahead
 * than that (James Leshikar's pedicure, an hour and a half on) is a check-in
 * for something else, and the line waits for its own seating. */
export const EARLY_CHECKIN_MS = 15 * 60000;

/* Where a line stands on a check-in made before its booked time:
 *   'waiting' — checked in early, within EARLY_CHECKIN_MS, and its time has not come;
 *   'due'     — the same, and its time has come;
 *   null      — anything else: no check-in, on time or late, or too far ahead. */
export function earlyCheckIn(startsAt, checkedInAt, now) {
  if (startsAt == null || checkedInAt == null || checkedInAt >= startsAt) return null;
  if (startsAt - checkedInAt > EARLY_CHECKIN_MS) return null;
  return startsAt > now ? 'waiting' : 'due';
}

/* The ids of the lines to put up now. `checkedInAt` is the booking system's
 * check-in instant for the visit, or null. */
export function sameTimeLines(lines, checkedInAt, now) {
  var anchors = {};
  var any = false, onBoard = false;
  lines.forEach(function (l) {
    if (l.onBoard) onBoard = true;
    if (l.started && l.startsAt != null) { anchors[l.startsAt] = true; any = true; }
  });

  if (!any) {
    // A held square is the desk's seating, waiting on the check-in to release
    // it; the release is what sets this off, not the check-in beside it.
    if (onBoard || checkedInAt == null) return [];
    // Every open service whose time has come, and none still to come. A
    // service checked in early counts from its booked time, so the check-in is
    // stale ten minutes after the later of the two.
    lines.forEach(function (l) {
      if (l.onBoard || l.closed || l.startsAt == null || l.startsAt > now) return;
      if (l.startsAt - checkedInAt > EARLY_CHECKIN_MS) return;
      if (now - Math.max(checkedInAt, l.startsAt) > CHECKIN_FRESH_MS) return;
      anchors[l.startsAt] = true;
    });
  }

  return lines.filter(function (l) {
    return !l.onBoard && !l.closed && l.startsAt != null && anchors[l.startsAt];
  }).map(function (l) { return l.id; });
}

/* The three answers to "Rosy is still busy", most likely first.
 *   'wait'  — leave it in Due now for that technician;
 *   'stop'  — stop their running square and start this one on them;
 *   'move'  — give it to somebody else (the assign sheet).
 * A request puts waiting first: the client asked for that person, and moving it
 * drops the request. Otherwise the floor keeps moving — somebody else first. */
export function busyChoices(requested) {
  return requested ? ['wait', 'stop', 'move'] : ['move', 'stop', 'wait'];
}

/* Whether auto-start leaves a booking for the desk even though the client has
 * checked in and it is booked with the seating. True only under turn order
 * (`followBook` false) and only when nobody was asked for by name.
 * `requested` is whether the booking carries a request for a technician. */
export function waitsForDesk(o) {
  var p = o || {};
  return p.followBook === false && !p.requested;
}
